import { useState, useEffect } from "react";
import StudyList from "./sidebar_tools/StudyList";
import ReportSummary from "./sidebar_tools/ReportSummary";
import "../styles/Datatable.css";

const ROWS_OPTIONS = [5, 10, 25, 50];
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const safeStr = (val) => (val == null ? "" : String(val));

const formatDate = (dateString) => {
  if (!dateString) return "";
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) return dateString;
  if (/^\d{8}$/.test(dateString)) {
    return `${dateString.substring(6, 8)}-${dateString.substring(4, 6)}-${dateString.substring(0, 4)}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  }

  const date = new Date(dateString);

  if (!isNaN(date.getTime())) {
    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}`;
  }

  return dateString;
};

export default function DataTable({ activeToolId = 0, onPatientSelect }) {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger] = useState(0);
  const [error, setError] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      setError("");

      const user = JSON.parse(localStorage.getItem("user"));
      const centerId = user?.center_id;

      let response;

      switch (user?.role) {
        case "center":
          response = await fetch(
            `${BASE_URL}/center/studies/?center_id=${centerId}`
          );
          break;

        case "assigner":
          response = await fetch(
            `${BASE_URL}/assigner/${user.id}/studies/`
          );
          break;

        case "doctor":
          response = await fetch(
            `${BASE_URL}/doctor/${user.id}/studies/`
          );
          break;

        default:
          throw new Error("Invalid user role");
      }

      if (!response.ok) {
        throw new Error("Failed to fetch patients");
      }

      const data = await response.json();
      const rows = Array.isArray(data) ? data : data.data || [];

      setTableData(
        rows.map((row) => ({
          ...row,
          study_id: row.id,
          patient_name: safeStr(row.patient_name),
          study: safeStr(row.study_description),
          study_description: safeStr(row.study_description),
          assigned: safeStr(row.doctor_name),
          center: safeStr(row.center_name),
          studyDate: formatDate(row.study_date),
          referral: "",
          status: safeStr(row.report_status),
          uploaded: "",
          signedOff: row.report_status === "signed_off" ? "Yes" : "No",
        }))
      );
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const totalRows = tableData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const activePage = Math.min(currentPage, totalPages);

  const startIndex = (activePage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;

    const left = Math.max(1, activePage - delta);
    const right = Math.min(totalPages, activePage + delta);

    if (left > 1) {
      pages.push(1);
      if (left > 2) pages.push("...");
    }

    for (let i = left; i <= right; i++) {
      pages.push(i);
    }

    if (right < totalPages) {
      if (right < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="data-table-container">
        <div className="dt-status">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="data-table-container">
        <div className="dt-status dt-error">
          {error}
          <button className="dt-retry-btn" onClick={fetchPatients}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      {activeToolId === 0 ? (
        <StudyList
          studyData={tableData}
          rowsPerPage={rowsPerPage}
          currentPage={activePage}
          setCurrentPage={setCurrentPage}
          onPatientSelect={onPatientSelect}
          refreshTrigger={refreshTrigger}
        />
      ) : (
        <ReportSummary
          studyData={tableData}
          rowsPerPage={rowsPerPage}
          currentPage={activePage}
          setCurrentPage={setCurrentPage}
        />
      )}

      <div className="pagination-bar">
        <div className="pagination-controls">
          <button
            className="page-btn"
            onClick={() => goToPage(activePage - 1)}
            disabled={activePage === 1}
          >
            ‹
          </button>

          {getPageNumbers().map((page, index) =>
            page === "..." ? (
              <span key={index} className="page-ellipsis">
                …
              </span>
            ) : (
              <button
                key={page}
                className={`page-btn ${
                  activePage === page ? "active" : ""
                }`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            )
          )}

          <button
            className="page-btn"
            onClick={() => goToPage(activePage + 1)}
            disabled={activePage === totalPages}
          >
            ›
          </button>
        </div>

        <div className="pagination-right">
          <span className="pagination-info">
            {totalRows === 0
              ? "No records"
              : `Results: ${startIndex + 1} - ${endIndex} of ${totalRows}`}
          </span>

          <select
            className="rows-select"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {ROWS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}